/**
 * Saved Content Service
 *
 * Domain layer service for the watch-later/saved-content feature. Reads
 * the local SavedContent records from encrypted storage and applies
 * Flutter's snapshot-time filter (!hiddenContent) plus the newest-first
 * sort.
 *
 * Singleton pattern for consistent state and efficient resource usage —
 * mirrors LikedVideosService and the other domain service singletons.
 *
 * Flutter parity:
 * - lib/presentation/screens/content/saved_content_screen.dart:69-91
 *   (getSavedContent: open Hive SavedContent box, filter
 *    `!(currentUser.hiddenContent ?? []).contains(savedContent.id)`,
 *    sort descending by createdDate).
 *
 * Differences from LikedVideosService:
 * - SavedContentStorage has no `liked` boolean — a save is a write and
 *   an unsave is a remove (see saveContentToWatchLaterUseCase). The
 *   filter therefore omits the `liked === true` check.
 */

import { SecureStorageWebCrypto } from '@/data/storage/local/secureStorageWebCrypto';
import { SECURE_STORAGE_KEYS } from '@/domain/entities/secureStorage/secureStorageKeys';
import type { SavedContentStorage } from '@/domain/entities/secureStorage/secureStorageEntities';
import type { UserEntity } from '@/domain/entities/user/userEntity';

export class SavedContentService {
  private static instance: SavedContentService;

  private constructor() { }

  public static getInstance(): SavedContentService {
    if (!SavedContentService.instance) {
      SavedContentService.instance = new SavedContentService();
    }
    return SavedContentService.instance;
  }

  /**
   * Read all locally-stored SavedContent records for the current user,
   * sorted newest first.
   *
   * Filter:
   * - records whose `id` (the media doc id, not the path) appears in
   *   `currentUser.hiddenContent` are dropped (Flutter line 83 compares
   *   `savedContent.id` against `hiddenContent`). A null/undefined
   *   `currentUser` or missing `hiddenContent` keeps everything (Flutter
   *   coalesces with `?? []`).
   *
   * Sort: descending by `createdDate` — Flutter line 87's
   * `a.createdDate < b.createdDate ? 1 : 0` pushes older entries down,
   * which is effectively newest-first.
   *
   * `currentUser` is taken as a parameter; domain services must not read
   * Redux directly (per `.claude/context/conventions.md`).
   *
   * Mirrors Flutter's getSavedContent in
   * lib/presentation/screens/content/saved_content_screen.dart:69-91.
   */
  async listLocalSavedContent(
    currentUser: UserEntity | null,
  ): Promise<SavedContentStorage[]> {
    const storage = SecureStorageWebCrypto.getInstance();
    const items = await storage.getAllWithPrefix<SavedContentStorage>(
      SECURE_STORAGE_KEYS.SAVED_CONTENT,
    );

    const hiddenContent = currentUser?.hiddenContent ?? [];

    const filtered = items.filter(
      (item) => !hiddenContent.includes(item.id),
    );

    return filtered.sort((a, b) => b.createdDate - a.createdDate);
  }
}

// Export singleton instance for convenience
export const savedContentService = SavedContentService.getInstance();
